if (!customElements.get("ona-video-carousel")) {
  class OnaVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.flicking = null;
      this.slideCount = 0;
    }
    connectedCallback() {
      const shadow = this.shadowRoot;
      shadow.innerHTML = `
        <style>
          :host {
            display: block;
            overflow: hidden;
          }
          .carousel-container {
            position: relative;
          }
          .flicking-viewport {
            position: relative;
            overflow: hidden;
          }
          .flicking-viewport.vertical {
            display: inline-flex;
          }
          .flicking-viewport.vertical > .flicking-camera {
            display: inline-flex;
            flex-direction: column;
          }
          .flicking-viewport.flicking-hidden > .flicking-camera > * {
            visibility: hidden;
          }
          .flicking-camera {
            width: 100%;
            height: 100%;
            display: flex;
            position: relative;
            flex-direction: row;
            z-index: 1;
            will-change: transform;
          }
          .flicking-camera > * {
            flex-shrink: 0;
          }
          .carousel-controls {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            margin-top: 16px;
            padding: 8px;
          }
          .carousel-nav-button {
            /* Reset default button styles */
            border: none;
            background: none;
            padding: 0;
            margin: 0;
            font: inherit;
            color: inherit;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            transition: all 0.3s ease;
          }
          .carousel-nav-button:focus-visible {
            outline: 2px solid var(--ona-video-carousel-focus-color);
            outline-offset: 2px;
          }
          .carousel-nav-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          /* Default dot styles - only applied when button has no custom content */
          .carousel-nav-button.default-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid #ccc;
            background: transparent;
          }
          .carousel-nav-button.default-dot:hover {
            border-color: #666;
            transform: scale(1.1);
          }
          .carousel-nav-button.default-dot.active {
            background: #007acc;
            border-color: #007acc;
          }
          .sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
          }
        </style>
        <div class="carousel-container">
          <div class="flicking-viewport"
            role="group"
            aria-roledescription="carousel"
            aria-label="Videos"
            aria-live="polite">
            <div class="flicking-camera"></div>
          </div>
          <div class="carousel-nav" part="nav" role="tablist" aria-label="Video navigation">
          </div>
        </div>
      `;
      this.setupCarousel();
    }
    setupCarousel() {
      const shadow = this.shadowRoot;
      const flickingViewport = shadow.querySelector(".flicking-viewport");
      const navContainer = shadow.querySelector(".carousel-nav");
      const slides = Array.from(this.querySelectorAll("ona-video-carousel-slide"));
      slides.forEach((slide) => {
        flickingViewport.firstElementChild.appendChild(slide);
      });
      this.slides = Array.from(flickingViewport.firstElementChild.children);
      this.slideCount = this.slides.length;
      this._createNavigationButtons(navContainer);
      this.flicking = new Flicking(flickingViewport, {
        circular: true,
        moveType: "snap",
        align: "center",
        panelsPerView: window.innerWidth > 767 ? 1.6 : 1.1,
        bound: true
      });
      this.flicking.on("ready", () => {
        this._updateActiveVideo(this.flicking.getStatus().index);
        this._updateActiveButton(this.flicking.getStatus().index);
      });
      this.flicking.on("changed", (e) => {
        const index = e.index;
        this._updateActiveVideo(index);
        this._updateActiveButton(index);
        this.dispatchEvent(
          new CustomEvent("slide-change", {
            detail: { index },
            bubbles: true,
            composed: true
          })
        );
      });
    }
    _createNavigationButtons(container) {
      container.innerHTML = "";
      for (let i = 0; i < this.slideCount; i++) {
        const button = document.createElement("button");
        button.className = "carousel-nav-button";
        button.setAttribute("part", "nav-button");
        button.setAttribute("role", "tab");
        button.setAttribute("aria-selected", i === 0 ? "true" : "false");
        button.setAttribute("aria-controls", `slide-${i}`);
        button.setAttribute("aria-label", `Go to video ${i + 1} of ${this.slideCount}`);
        button.setAttribute("data-index", i);
        button.setAttribute("tabindex", i === 0 ? "0" : "-1");
        const customButtonContent = this.querySelector(`[slot="nav-button-${i}"]`);
        if (customButtonContent) {
          const clonedContent = customButtonContent.cloneNode(true);
          clonedContent.removeAttribute("slot");
          button.appendChild(clonedContent);
        } else {
          button.classList.add("default-dot");
          const srText = document.createElement("span");
          srText.className = "sr-only";
          srText.textContent = `Video ${i + 1}`;
          button.appendChild(srText);
        }
        button.addEventListener("click", () => {
          this.goToSlide(i);
        });
        button.addEventListener("keydown", (e) => {
          this.handleButtonKeydown(e, i);
        });
        container.appendChild(button);
      }
      if (container.firstElementChild) {
        container.firstElementChild.classList.add("active");
        container.firstElementChild.setAttribute("part", "nav-button active");
      }
    }
    handleButtonKeydown(event, currentIndex) {
      const buttons = this.shadowRoot.querySelectorAll(".carousel-nav-button");
      let targetIndex = currentIndex;
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          targetIndex = currentIndex > 0 ? currentIndex - 1 : this.slideCount - 1;
          break;
        case "ArrowRight":
          event.preventDefault();
          targetIndex = currentIndex < this.slideCount - 1 ? currentIndex + 1 : 0;
          break;
        case "Home":
          event.preventDefault();
          targetIndex = 0;
          break;
        case "End":
          event.preventDefault();
          targetIndex = this.slideCount - 1;
          break;
        default:
          return;
      }
      buttons[targetIndex].focus();
      this.goToSlide(targetIndex);
    }
    goToSlide(index) {
      if (this.flicking && index >= 0 && index < this.slideCount) {
        this.flicking.moveTo(index);
      }
    }
    _updateActiveButton(activeIndex) {
      const buttons = this.shadowRoot.querySelectorAll(".carousel-nav-button");
      buttons.forEach((button, i) => {
        const isActive = i === activeIndex;
        button.classList.toggle("active", isActive);
        button.setAttribute("part", isActive ? "nav-button active" : "nav-button");
        button.setAttribute("aria-selected", isActive ? "true" : "false");
        button.setAttribute("tabindex", isActive ? "0" : "-1");
      });
    }
    _updateActiveVideo(activeIndex) {
      this.slides.forEach((slide, i) => {
        const videoSlot = slide.querySelector("ona-video-slot");
        if (!videoSlot) return;
        slide.setAttribute("id", `slide-${i}`);
        const isActive = i === activeIndex;
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
        if (isActive) {
          slide.setAttribute("tabindex", "0");
          this._setFocusableElements(slide, true);
        } else {
          slide.removeAttribute("tabindex");
          this._setFocusableElements(slide, false);
        }
        if (isActive) {
          videoSlot.play().catch(() => {
          });
          this.dispatchEvent(
            new CustomEvent("video-play", {
              detail: { index: i, videoSlot },
              bubbles: true,
              composed: true
            })
          );
        } else {
          videoSlot.stop();
          this.dispatchEvent(
            new CustomEvent("video-stop", {
              detail: { index: i, videoSlot },
              bubbles: true,
              composed: true
            })
          );
        }
      });
    }
    _setFocusableElements(container, focusable) {
      const focusableSelector = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
      const focusableElements = container.querySelectorAll(focusableSelector);
      focusableElements.forEach((element) => {
        if (focusable) {
          const originalTabindex = element.getAttribute("data-original-tabindex");
          if (originalTabindex !== null) {
            if (originalTabindex === "null") {
              element.removeAttribute("tabindex");
            } else {
              element.setAttribute("tabindex", originalTabindex);
            }
            element.removeAttribute("data-original-tabindex");
          }
        } else {
          const currentTabindex = element.getAttribute("tabindex");
          element.setAttribute("data-original-tabindex", currentTabindex || "null");
          element.setAttribute("tabindex", "-1");
        }
      });
    }
    // Public API methods
    next() {
      if (this.flicking) {
        this.flicking.next();
      }
    }
    prev() {
      if (this.flicking) {
        this.flicking.prev();
      }
    }
    getCurrentIndex() {
      return this.flicking ? this.flicking.getStatus().index : 0;
    }
    getTotalSlides() {
      return this.slideCount;
    }
  }
  class OnaVideoCarouselSlide extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }
    connectedCallback() {
      this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          box-sizing: border-box;
          padding: 0 calc(var(--ona-video-carousel-gap, 0) / 2);
        }

        ::slotted(*) {
          width: 100%;
          height: auto;
        }
      </style>
      <slot></slot>
    `;
    }
  }
  customElements.define("ona-video-carousel-slide", OnaVideoCarouselSlide);
  customElements.define("ona-video-carousel", OnaVideoCarousel);
}
